import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { TabContent, TabPane } from 'reactstrap';
import { useNavigate } from 'react-router';
import { Operation } from '../../../services/types/Operation';
import { SpinnerArea, TabBar } from '../../../shared/components';
import GIEsInfo from './GIEsInfo';
import GIFinantialData from './GIFinantialData';
import GIOperationInfo from './GIOperationInfo';

interface Props {
  operation: Operation;
  loading: boolean;
}

const GeneralInformationTabContainer: React.FC<Props> = ({ operation, loading }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(1);
  const navigate = useNavigate();

  const tabHeaders = [
    { id: 1, text: t('esInformation'), htmlId: 'tabEsInformation' },
    { id: 2, text: t('operationInformation'), htmlId: 'tabOperationInformations' },
    { id: 3, text: t('financialData'), htmlId: 'tabFinancialData' },
  ];

  if (operation.indicatorsApply) {
    tabHeaders.push(
      { id: 4, text: t('esgIndicators'), htmlId: 'tabIndicators' },
      { id: 5, text: t('ghgCalculator'), htmlId: 'tabCalculator' }
    );
  }

  useEffect(() => {
    if (activeTab === 4) {
      navigate(`/operations/${operation.number}/indicator`);
    }
    if (activeTab === 5) {
      navigate(`/operations/${operation.number}/calculator`);
    }
  }, [activeTab, navigate, operation.number]);

  useEffect(() => {
    if (!operation.indicatorsApply && activeTab > 3) {
      setActiveTab(1); // Reset to default tab if the current tab is no longer applicable
    }
  }, [operation.indicatorsApply, activeTab]);

  const toggle = (tabId: number) => {
    if (activeTab !== tabId) {
      setActiveTab(tabId);
    }
  };

  return (
    <SpinnerArea loading={loading}>
      {operation && (
        <>
          <TabBar
            id="operationGeneralInformation"
            tabSelected={activeTab}
            setTabSelected={toggle}
            headers={tabHeaders}
          />
          <TabContent activeTab={activeTab}>
            {tabHeaders.map((tab) => (
              <TabPane key={tab.id} tabId={tab.id}>
                {tab.id === 1 && <GIEsInfo operation={operation} />}
                {tab.id === 2 && <GIOperationInfo operation={operation} />}
                {tab.id === 3 && <GIFinantialData operation={operation} />}
              </TabPane>
            ))}
          </TabContent>
        </>
      )}
    </SpinnerArea>
  );
};

export default GeneralInformationTabContainer;
